import { Select } from '@thanx/uikit/select'
import { RevenueCenter, RevenueCenterState } from 'api/revenueCenters'
import { buildTranslate } from 'locales'
import React, { useMemo } from 'react'

const t = buildTranslate('cms.settings.revenue_centers')

type Option = {
  value: string
  label: string
}

type Props = {
  revenueCenters: RevenueCenter[]
  // The currently selected Revenue Center name, or undefined when unset.
  value: string | undefined
  onChange: (name: string | undefined) => void
  placeholder?: string
  ariaLabel?: string
  ariaLabelledBy?: string
  disabled?: boolean
}

// Preference order when one name appears in multiple states: an ACTIVE entry
// wins over UNKNOWN, which wins over ARCHIVED. Keeps the representative
// independent of response order and drives the label below.
const STATE_RANK: Record<RevenueCenterState, number> = {
  ACTIVE: 2,
  UNKNOWN: 1,
  ARCHIVED: 0,
}

// Toast returns one catalog entry per location GUID, so the same logical
// Revenue Center name can appear many times — and can be ACTIVE at some
// locations while ARCHIVED at others. Merchants select by name, so the picker
// dedups to distinct names and keeps the highest-ranked entry, so the archived
// suffix is only shown when the name is archived everywhere (not by response
// order).
function dedupeByName(revenueCenters: RevenueCenter[]): Option[] {
  const byName = new Map<string, RevenueCenter>()
  revenueCenters.forEach(rc => {
    const existing = byName.get(rc.name)
    if (!existing || STATE_RANK[rc.state] > STATE_RANK[existing.state]) {
      byName.set(rc.name, rc)
    }
  })
  return Array.from(byName.values(), rc => ({
    value: rc.name,
    label:
      rc.state === 'ARCHIVED'
        ? `${rc.name}${t('row.archived_suffix')}`
        : rc.name,
  }))
}

const RevenueCenterPicker = ({
  revenueCenters,
  value,
  onChange,
  placeholder,
  ariaLabel,
  ariaLabelledBy,
  disabled,
}: Props) => {
  const options = useMemo(() => dedupeByName(revenueCenters), [revenueCenters])
  const selected = useMemo(
    () => (value ? options.find(o => o.value === value) ?? null : null),
    [value, options]
  )

  return (
    <Select
      options={options}
      value={selected ? [selected] : []}
      onChange={params => onChange(params[0]?.value ?? undefined)}
      placeholder={placeholder ?? t('row.placeholder')}
      disabled={disabled}
      searchable
      // Prefer an associated visible label; fall back to an explicit name.
      aria-label={
        ariaLabelledBy ? undefined : ariaLabel ?? t('default_tab.field_label')
      }
      aria-labelledby={ariaLabelledBy}
    />
  )
}

export default RevenueCenterPicker
